import type { Item, Skill } from '../content/types.ts';

/**
 * The Hotlist: the crawler's ten quick-access slots.
 *
 * RULE DCC-HOT-001 - ten slots. Rules for Survival p.48: "By default, each
 * crawler's Hotlist contains 10 slots for items, Spells, and anything else the
 * crawler wants to access quickly." Crawlers & Customization p.15 states it
 * without the hedge: "Every crawler has a 10-slot Hotlist."
 *
 * RULE DCC-HOT-002 - the Hotlist is "a special part of their Inventory"
 * (Rules p.48), so an entry names something the crawler already has. It is not
 * a container that can manufacture content.
 *
 * RULE DCC-HOT-018 - Hotlist membership grants NOTHING mechanically. C&C p.15:
 * "Items in Hotlists do not grant you any added benefits: they're just easily
 * accessible." This is why an entry carries a reference and nothing else, and
 * why an entry naming content this installation lacks is inert rather than
 * dangerous - the opposite of an equipped Item, which feeds Effects.
 *
 * THIS FILE IS CONFIGURATION ONLY. Nothing here activates, consumes, swaps or
 * spends an Action. Using a slot is somebody else's job, and WHICH somebody
 * depends on the operation - they are not one pathway:
 *
 *  - drinking a prepared supported consumable is the ordinary item-use
 *    pathway (`rules/itemUse.ts` + `app/itemUseService.ts`), NOT
 *    `hotlistUse.ts`;
 *  - putting an Item physically into a hand, or exchanging one for it, is the
 *    paid Ready / Swap Action in `rules/hotlistUse.ts` (E5);
 *  - swapping a weapon free as part of an Attack is `rules/weaponSwap.ts` (E3);
 *  - casting from a slot is E6.
 *
 * The one thing this file does contribute is `hotlistDirectUseAvailable`, which
 * answers whether a slot is even eligible for the first of those. It decides
 * nothing about what the drink does.
 */

/** RULE DCC-HOT-001 - Rules for Survival p.48, C&C p.15. */
export const HOTLIST_SLOTS = 10;

/**
 * RULE DCC-HOT-019 - how many copies of one Item may share a slot.
 *
 * C&C p.15: "You may place up to 999 of the same item by name into a single
 * slot of your Hotlist, because having 1,000 of something is just greedy."
 */
export const HOTLIST_MAX_ITEM_QUANTITY = 999;

/**
 * What a slot points at.
 *
 * Three supported reference kinds: physical Items, Spells, and ordinary Skills.
 * C&C p.15 explicitly lists Spells and Items, while Rules p.48 permits "anything
 * else the crawler wants to access quickly." Ordinary Skills now have a real
 * executable UI pathway, so they are no longer a placeholder concept.
 *
 * Skill and Spell arms both use `skillId`; there is NO separate Spell ID namespace. A
 * Spell in this project is Skill content with `kind === 'spell'`, which is the
 * identity everything else already references. Validation confirms the
 * referenced Skill really IS a Spell rather than trusting the arm's name, the
 * same way weapon links confirm an Attack is a held weapon.
 */
export type HotlistEntry =
  | {
      kind: 'item';
      itemId: string;
      /**
       * How many copies sit in this slot. RULE DCC-HOT-019 - C&C p.15 puts up
       * to 999 of one item in a single slot, and C&C p.13 has the five starting
       * Mana Potions written into one.
       *
       * PERSISTED AND EXPLICIT, never inferred as "all owned copies". Inferring
       * it would mean picking up a sixth potion silently added it to the
       * Hotlist - possibly mid-combat - with no Retrieve having happened.
       *
       * It is also what makes the Hotlist answerable about PHYSICAL objects. A
       * crawler owning one Longsword and wielding it has no spare to put here;
       * without a count the application cannot tell "a second sword is stowed
       * ready" from "a pointer at the one already in my hand", and the free
       * weapon swap has no way to know whether there is anything to swap TO.
       */
      quantity: number;
    }
  | { kind: 'spell'; skillId: string }
  | { kind: 'skill'; skillId: string };

/** A slot holds one entry or nothing. Empty slots are real, not absent. */
export type HotlistSlot = HotlistEntry | null;

/** Exactly `HOTLIST_SLOTS` long. The length IS the capacity rule. */
export type Hotlist = HotlistSlot[];

/** Ten empty slots, for a new or migrated crawler. */
export function emptyHotlist(): Hotlist {
  return Array.from({ length: HOTLIST_SLOTS }, () => null);
}

/** The stable ID an entry points at, whichever arm it is. */
export function hotlistEntryId(entry: HotlistEntry): string {
  return entry.kind === 'item' ? entry.itemId : entry.skillId;
}

/** Whether two entries name the same thing. Arms differ, so kind counts. */
export function sameHotlistEntry(a: HotlistEntry, b: HotlistEntry): boolean {
  return a.kind === b.kind && hotlistEntryId(a) === hotlistEntryId(b);
}

/** Structural check, no catalogue and no crawler needed. */
export function isHotlistEntry(value: unknown): value is HotlistEntry {
  if (typeof value !== 'object' || value === null) return false;
  const record = value as Record<string, unknown>;
  if (record['kind'] === 'item') {
    const quantity = record['quantity'];
    // A whole number from 1 to 999. RULE DCC-HOT-019 - C&C p.15 caps a slot at
    // 999, so 1000 is not a preserved state, it is a MALFORMED one. Preserved
    // states are legitimate things this application produced and cannot
    // currently satisfy; a slot holding a thousand of something was never
    // reachable through any released pathway, and treating it as valid would
    // let it display as usable.
    return (
      typeof record['itemId'] === 'string' &&
      typeof quantity === 'number' &&
      Number.isInteger(quantity) &&
      quantity >= 1 &&
      quantity <= HOTLIST_MAX_ITEM_QUANTITY
    );
  }
  // Skill/Spell entries carry NO quantity. They are not physical objects and
  // are not consumed by use; there is nothing to count.
  if (record['kind'] === 'spell' || record['kind'] === 'skill') return typeof record['skillId'] === 'string';
  return false;
}

/**
 * Structural problems with a whole Hotlist. Catalogue-INDEPENDENT on purpose:
 * this runs in `validateCharacter`, which must not reach for a registry, and an
 * entry naming content this installation lacks is a state worth preserving.
 *
 * THE LINE IS MALFORMED VERSUS UNSATISFIABLE, not lenient versus strict.
 *
 * CHECKED here, because they are structure and no released pathway could have
 * produced them, so rejecting one discards nothing the application made:
 *  - exactly ten slots;
 *  - an Item quantity that is a whole number from 1 to 999 (RULE DCC-HOT-019);
 *  - no two slots naming the same thing (also DCC-HOT-019).
 *
 * A duplicate is not merely untidy. Each slot is checked against available
 * copies ONE AT A TIME, so two slots each holding one Longsword can both report
 * themselves usable while the crawler owns a single sword. It also breaks Undo:
 * clearing one duplicate records the other as the inverse, and restoring it is
 * then refused by the duplicate rule - so "Undo restores the exact previous
 * state" would be false for a state the validator had waved through.
 *
 * NOT CHECKED here, because these are ownership and dependency questions whose
 * answers change with the crawler and the installation:
 *  - whether a reference resolves to content this machine has;
 *  - whether the crawler still owns enough copies to back the allocation.
 * Both stay loadable, visible and INERT. Refusing to open somebody's crawler
 * because a shortcut points at a missing custom Spell repeats exactly the
 * mistake RULE DCC-INV-012 exists to prevent, and by DCC-HOT-018 the entry does
 * nothing while it sits there.
 */
export function validateHotlist(value: unknown): string[] {
  const problems: string[] = [];
  if (!Array.isArray(value)) return ['hotlist must be a list.'];
  if (value.length !== HOTLIST_SLOTS) {
    problems.push(`hotlist must have exactly ${HOTLIST_SLOTS} slots, not ${value.length}.`);
  }
  const entries: { entry: HotlistEntry; index: number }[] = [];
  value.forEach((slot, index) => {
    if (slot === null) return;
    if (!isHotlistEntry(slot)) {
      problems.push(`hotlist slot ${index + 1} is not a valid entry.`);
      return;
    }
    entries.push({ entry: slot, index });
  });

  // RULE DCC-HOT-019 - one slot per distinct thing. Reported against the LATER
  // slot, so the message points at the copy rather than the original.
  entries.forEach(({ entry, index }, position) => {
    const earlier = entries
      .slice(0, position)
      .find((other) => sameHotlistEntry(other.entry, entry));
    if (earlier) {
      problems.push(
        `hotlist slot ${index + 1} repeats what is already in slot ${earlier.index + 1}.`,
      );
    }
  });

  return problems;
}

export interface HotlistCatalogue {
  lookupItem: (itemId: string) => Item | undefined;
  lookupSkill: (skillId: string) => Skill | undefined;
}

/** What the crawler holds, for ownership checks. Not the whole Character, so
 *  this file stays usable from the rules layer without a circular import. */
export interface HotlistOwnership {
  /**
   * Copies of an Item that are in NO Gear Slot and NO hand:
   * `quantity - equipped - held`, which is `unallocatedCopies`
   * (RULE DCC-INV-019). Never computed by subtracting only `equipped`.
   *
   * The subtraction is the whole point. A crawler owning one Longsword and
   * wielding it has zero available, so that sword cannot also be sitting in the
   * Hotlist - there is one physical object and it is in their hand. C&C p.15 is
   * explicit that a Hotlisted item's benefits are OFF and that swapping moves
   * one weapon out as another moves in, which only makes sense if the two
   * places hold different copies.
   */
  availableCopies: (itemId: string) => number;
  knowsSkill: (skillId: string) => boolean;
}

/** Copies of an Item already allocated to Hotlist slots. Duplicates are
 *  refused, so in practice this is one slot - summed anyway rather than
 *  assuming an invariant enforced elsewhere. */
export function hotlistAllocation(hotlist: Hotlist, itemId: string): number {
  return hotlist.reduce(
    (total, slot) =>
      slot !== null && slot.kind === 'item' && slot.itemId === itemId
        ? total + slot.quantity
        : total,
    0,
  );
}

/**
 * THE ALLOCATION INVARIANT: `equipped + held + hotlisted <= owned` (RULE DCC-INV-019).
 *
 * Three terms since E5B, not two: a copy physically in a hand is allocated as
 * surely as a worn one. `held` was added rather than folded into `equipped`
 * because only a Gear Slot grants Effects.
 *
 * Every copy is a physical object in exactly one place. Returns the problem
 * with a proposed inventory quantity, or null.
 *
 * Enforced on ORDINARY player operations only. A preserved, imported or undone
 * state that breaks it stays loadable and inert - the same acquisition-versus-
 * preservation split `add-item` and `set-item` make under RULE DCC-INV-012 -
 * because deleting somebody's shortcut or silently unequipping their armour to
 * make the numbers work is worse than showing them a slot they must sort out.
 */
export function allocationProblem(
  hotlist: Hotlist,
  itemId: string,
  itemName: string,
  proposedQuantity: number,
  equipped: number,
  /**
   * RULE DCC-INV-018 - copies physically in a hand without being in their Gear
   * Slot. THE THIRD ALLOCATION. A potion drawn from the Hotlist is in neither
   * `equipped` nor the Hotlist, and counting only the first two would let the
   * same physical copy back a slot as well.
   */
  held: number,
): string | null {
  const hotlisted = hotlistAllocation(hotlist, itemId);
  const allocated = equipped + held + hotlisted;
  if (allocated <= proposedQuantity) return null;
  const parts = [
    `${equipped} equipped`,
    ...(held > 0 ? [`${held} in hand`] : []),
    `${hotlisted} in the Hotlist`,
  ];
  return (
    `${itemName}: ${parts.join(', ')} is ${allocated} copies, but that would leave only ` +
    `${proposedQuantity}. Clear or reduce the Hotlist slot first.`
  );
}

/**
 * Why an Item cannot be removed from the Inventory ENTIRELY, or null when it can.
 *
 * SHARED, because the panel and the service were answering different questions
 * about the same button. `removeItem` deliberately takes equipped AND held
 * copies with it, so the state it must be asked about is the one AFTERWARDS:
 * nothing owned, nothing worn, nothing in hand. The panel was asking about the
 * CURRENT hand instead, so a crawler carrying their only helmet was told the
 * removal would take it - and then handed a disabled button.
 *
 * What genuinely blocks a removal is a Hotlist slot that would be left claiming
 * copies the crawler no longer has. That slot is never edited by an operation
 * nobody asked to edit it with; the player is told to clear it first.
 */
export function removeItemProblem(
  hotlist: Hotlist,
  itemId: string,
  itemName: string,
): string | null {
  // Zero owned, zero equipped, zero held: whole-entry removal takes the lot.
  return allocationProblem(hotlist, itemId, itemName, 0, 0, 0);
}

/**
 * Why an entry may not be put in a slot by an ordinary Add, or null when it may.
 *
 * This is the ACQUISITION pathway, and it is strict. The preservation pathway -
 * an Undo restoring an entry, or an import carrying one - is deliberately
 * looser, exactly as `add-item` and `set-item` differ for Inventory under RULE
 * DCC-INV-012. See `set-hotlist-slot` in `src/rules/characterChanges.ts`.
 */
export function hotlistAddProblem(
  hotlist: Hotlist,
  slot: number,
  entry: HotlistEntry,
  catalogue: HotlistCatalogue,
  ownership: HotlistOwnership,
): string | null {
  const slotProblem = hotlistSlotIndexProblem(slot);
  if (slotProblem) return slotProblem;

  const duplicate = hotlistDuplicateProblem(hotlist, slot, entry);
  if (duplicate) return duplicate;

  if (entry.kind === 'item') {
    const item = catalogue.lookupItem(entry.itemId);
    if (!item) return `There is no Item with the ID "${entry.itemId}".`;

    if (!Number.isInteger(entry.quantity) || entry.quantity < 1) {
      return 'A Hotlist slot must hold a whole number of at least 1. Clear the slot instead.';
    }
    if (entry.quantity > HOTLIST_MAX_ITEM_QUANTITY) {
      return `A Hotlist slot holds at most ${HOTLIST_MAX_ITEM_QUANTITY} of one item (C&C p.15).`;
    }

    // RULE DCC-HOT-002 - the Hotlist is part of the Inventory, so it can only
    // hold things the crawler actually has. EQUIPPED COPIES DO NOT COUNT: one
    // Longsword in a hand is not also a spare in the Hotlist.
    const available = ownership.availableCopies(entry.itemId);
    if (available <= 0) {
      return `${item.name} has no copies free. The Hotlist holds things the crawler owns and is not already wearing or holding (Rules p.48).`;
    }
    if (entry.quantity > available) {
      return `Only ${available} ${item.name} ${available === 1 ? 'is' : 'are'} free to put in the Hotlist.`;
    }
    return null;
  }

  const skill = catalogue.lookupSkill(entry.skillId);
  if (!skill) return `There is no Skill with the ID "${entry.skillId}".`;
  if (entry.kind === 'spell' && skill.kind !== 'spell') {
    return `${skill.name} is not a Spell, so it cannot go in a Hotlist Spell slot.`;
  }
  if (entry.kind === 'skill' && skill.kind === 'spell') {
    return `${skill.name} is a Spell, so it must use a Hotlist Spell slot.`;
  }
  if (!ownership.knowsSkill(entry.skillId)) {
    return `This crawler does not know ${skill.name}.`;
  }
  return null;
}

/** Where an outgoing held Item lands in the Hotlist, or why it has nowhere. */
export type HotlistReturnHome = { slot: number; merge: boolean } | { reason: string };

/**
 * RULE DCC-HOT-012 - a Hotlist exchange is an EXCHANGE. Where does the Item
 * coming OUT of the hand go?
 *
 * SHARED, because two priced pathways ask it and the answer must not differ
 * between them: E3's free weapon swap during an Attack, and E5's paid Use a
 * Hotlist Item exchange. It was written once inside `weaponSwap.ts` and is
 * lifted here rather than copied, because a second implementation of one rule
 * is how the two would quietly stop agreeing.
 *
 * Three cases, and only the first is described by the source:
 *
 *  1. The incoming slot is emptied by the exchange (it held exactly one copy).
 *     The outgoing Item takes that slot. This is the printed exchange: one
 *     moves out, one moves in, and the slot count never changes.
 *  2. The outgoing Item ALREADY has a Hotlist slot. It joins that stack,
 *     because two slots naming one thing is refused (RULE DCC-HOT-019).
 *  3. The incoming slot still holds copies and the outgoing Item has no slot of
 *     its own. It needs an empty one.
 *
 * Case 3 is where the books go quiet. With no empty slot the operation is
 * REFUSED rather than guessed at - dropping the Item on the floor, deleting it,
 * or overflowing into an eleventh slot are all inventions, and DCC-HOT-006
 * already says a full Hotlist needs room made first.
 */
export function hotlistHomeForReturn(
  hotlist: Hotlist,
  incomingSlot: number,
  incomingQuantity: number,
  outgoingItemId: string,
): HotlistReturnHome {
  const existing = hotlist.findIndex(
    (entry, index) =>
      index !== incomingSlot &&
      entry !== null &&
      entry.kind === 'item' &&
      entry.itemId === outgoingItemId,
  );
  if (existing !== -1) {
    const entry = hotlist[existing] ?? null;
    if (entry !== null && entry.kind === 'item' && entry.quantity >= HOTLIST_MAX_ITEM_QUANTITY) {
      return { reason: `Hotlist slot ${existing + 1} already holds ${HOTLIST_MAX_ITEM_QUANTITY}.` };
    }
    return { slot: existing, merge: true };
  }

  if (incomingQuantity === 1) return { slot: incomingSlot, merge: false };

  const empty = hotlist.findIndex((entry, index) => index !== incomingSlot && entry === null);
  if (empty === -1) {
    return {
      reason:
        'There is no free Hotlist slot for the Item being put away, and that slot still holds ' +
        'other copies. Clear a slot first (Rules p.48).',
    };
  }
  return { slot: empty, merge: false };
}

/**
 * How many copies to PREPARE by default when an Item is chosen for a slot.
 *
 * RULE DCC-HOT-022 - PROJECT DECISION, informed by the source. C&C p.15 allows
 * "up to 999 of the same item by name into a single slot", and C&C p.13 puts a
 * spellcaster's five starting Mana Potions in ONE slot - so preparing a whole
 * stack of consumables is the printed, expected case, and a control that
 * defaults to 1 makes the printed case the one the player has to work for.
 *
 * A default of 1 is not merely inconvenient: it looks like the stack was
 * prepared and then vanished after a single use, because the slot really did
 * empty. That is what it looked like on Windows.
 *
 * Consumables therefore default to EVERY free copy; anything else defaults to
 * ONE. Allocating a crawler's whole stack of ordinary gear because they happen
 * to own four daggers would be the opposite mistake, and E2's explicit-
 * allocation principle exists to prevent it.
 *
 * PURE, so the React handler owns no rule-like arithmetic and the default can
 * be regression-tested without rendering anything.
 *
 * @param available copies free to allocate: owned, less equipped, less held.
 */
export function defaultHotlistAllocation(
  item: { consumable: boolean } | undefined,
  available: number,
): number {
  if (available <= 0) return 1;
  const capped = Math.min(available, HOTLIST_MAX_ITEM_QUANTITY);
  return item?.consumable === true ? capped : 1;
}

export function hotlistSlotIndexProblem(slot: number): string | null {
  if (!Number.isInteger(slot) || slot < 0 || slot >= HOTLIST_SLOTS) {
    return `A Hotlist slot must be a whole number from 1 to ${HOTLIST_SLOTS}.`;
  }
  return null;
}

/**
 * RULE DCC-HOT-019 - one slot per distinct thing.
 *
 * SOURCE-BACKED, and this was recorded as an open question until the page was
 * read as an image: C&C p.15's Hotlist sidebar says "You may place up to 999 of
 * the same item by name into a single slot of your Hotlist, because having
 * 1,000 of something is just greedy." Many of one thing occupy ONE slot, so a
 * second slot naming the same thing is not something the printed model has room
 * for. C&C p.13 says the same thing in passing for the five starting Mana
 * Potions: "Write all the potions in one Hotlist slot."
 *
 * It is a strong steer rather than a stated prohibition, so it is recorded as a
 * project decision informed by the source rather than as a printed rule. It
 * also costs the player nothing: a duplicate is one shortcut and one wasted
 * slot out of ten.
 */
export function hotlistDuplicateProblem(
  hotlist: Hotlist,
  slot: number,
  entry: HotlistEntry,
): string | null {
  const clash = hotlist.findIndex(
    (existing, index) => index !== slot && existing !== null && sameHotlistEntry(existing, entry),
  );
  if (clash === -1) return null;
  return `That is already in Hotlist slot ${clash + 1}. One slot holds up to 999 of the same thing (C&C p.15).`;
}

/** How a slot should be shown, and whether it can still be used later. */
export type HotlistSlotStatus =
  | { state: 'empty' }
  /** Resolves, and the crawler has the copies to back it. */
  | { state: 'ok'; label: string; kind: HotlistEntry['kind']; quantity: number | null }
  /** This installation has no such content. Preserved, inert, removable. */
  | { state: 'unresolved'; label: string; kind: HotlistEntry['kind']; quantity: number | null }
  /**
   * Resolves, but the copies are not there: sold, drunk, or already equipped.
   * Preserved, inert, removable - never silently shrunk or deleted.
   */
  | {
      state: 'unavailable';
      label: string;
      kind: HotlistEntry['kind'];
      quantity: number | null;
      available: number;
    };

/**
 * What to show for a slot.
 *
 * `unresolved` and `not-owned` are DISPLAY states, never deletions. A shortcut
 * to a custom Spell this machine lacks, or to the last potion the crawler just
 * drank, stays visible and removable rather than vanishing. By DCC-HOT-018 it
 * grants nothing while it sits there, so keeping it is free.
 */
/**
 * RULE DCC-HOT-007 - whether a slot can be DRUNK FROM directly, right now.
 *
 * Two questions, and both must be yes:
 *
 *  1. **Is the Item capable of it?** Only an authored `use` arm executes. E4
 *     deliberately refuses to make an arbitrary potion executable, so
 *     `consumable` alone is not enough - a button for an Item with no `use`
 *     would promise something the service refuses.
 *  2. **Is the SLOT legal?** `hotlistSlotStatus` returns `unavailable` for a
 *     preserved over-allocated entry - one claiming more copies than the
 *     crawler has free. E2 promised such a slot stays visible, removable and
 *     INERT, and offering a live Use on it breaks that promise from the
 *     interface side even though the service would refuse. A control that the
 *     authoritative planner rejects is a lie the player finds out about by
 *     clicking it.
 *
 * PURE, so both halves are regression-tested without rendering anything, and
 * the panel keeps no rule of its own.
 *
 * It does not repair, shrink or otherwise touch an unavailable slot. Inert
 * means inert.
 */
export function hotlistDirectUseAvailable(
  status: HotlistSlotStatus,
  item: Item | undefined,
): boolean {
  if (status.state !== 'ok' || status.kind !== 'item') return false;
  return item !== undefined && item.consumable && item.use !== undefined;
}

export function hotlistSlotStatus(
  entry: HotlistSlot,
  catalogue: HotlistCatalogue,
  ownership: HotlistOwnership,
): HotlistSlotStatus {
  if (entry === null) return { state: 'empty' };

  if (entry.kind === 'item') {
    const item = catalogue.lookupItem(entry.itemId);
    if (!item) {
      return { state: 'unresolved', label: entry.itemId, kind: 'item', quantity: entry.quantity };
    }
    // ALLOCATION, not mere ownership. A slot holding one Longsword is not
    // usable when the crawler owns exactly one and it is already in their hand.
    const available = ownership.availableCopies(entry.itemId);
    if (available < entry.quantity) {
      return {
        state: 'unavailable',
        label: item.name,
        kind: 'item',
        quantity: entry.quantity,
        available,
      };
    }
    return { state: 'ok', label: item.name, kind: 'item', quantity: entry.quantity };
  }

  const skill = catalogue.lookupSkill(entry.skillId);
  if (!skill) return { state: 'unresolved', label: entry.skillId, kind: entry.kind, quantity: null };
  // A reference whose persisted arm no longer matches the loaded definition is
  // preserved but inert. This protects imported/custom content from being
  // silently reinterpreted after an edit.
  if ((entry.kind === 'spell' && skill.kind !== 'spell') || (entry.kind === 'skill' && skill.kind === 'spell')) {
    return { state: 'unresolved', label: skill.name, kind: entry.kind, quantity: null };
  }
  if (!ownership.knowsSkill(entry.skillId)) {
    return { state: 'unavailable', label: skill.name, kind: entry.kind, quantity: null, available: 0 };
  }
  return { state: 'ok', label: skill.name, kind: entry.kind, quantity: null };
}
